import { ButtonGroupNew, DateRangePicker, DropdownNew, LuneThemeNew } from '@lune-fe/lune-ui-lib'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import moment from 'moment'
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Range } from 'react-date-range'

import useUpdateEffect from 'hooks/useUpdateEffect'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

const { palette } = LuneThemeNew

export enum PredefinedRange {
    THIRTY_DAYS = '30',
    NINETY_DAYS = '90',
    TWELVE_MONTHS = '12',
    CUSTOM = 'custom',
}

const predefinedRanges = [
    {
        label: 'Last 30 days',
        value: PredefinedRange.THIRTY_DAYS,
    },
    {
        label: 'Last 90 days',
        value: PredefinedRange.NINETY_DAYS,
    },
    {
        label: 'Last 12 months',
        value: PredefinedRange.TWELVE_MONTHS,
    },
]

const DATE_FORMAT = 'DD MMM YYYY'

const DateRangeFilterNew: FC<{
    setDateRange: Dispatch<SetStateAction<Range | undefined>>
    defaultRange?: PredefinedRange
    defaultLabel?: string
    dateDisplayFormat?: string
    sx?: SxProps
    labelSx?: SxProps
    predefinedDateRange?: Range
}> = ({
    setDateRange,
    defaultRange,
    defaultLabel,
    dateDisplayFormat,
    sx,
    labelSx,
    predefinedDateRange,
}) => {
    const today = useMemo(() => moment(), [])
    const [predefinedRange, setPredefinedRange] = useState<string | undefined>(defaultRange)
    const [buttonLabel, setButtonLabel] = useState<string>()
    const [localDateRange, setLocalDateRange] = useState<Range>({
        startDate: undefined,
        endDate: undefined,
    })
    const [initLoad, setInitLoad] = useState<boolean>(true)
    const EMPTY_PLACEHOLDER = 'Select date range'

    const getButtonLabel = (selectedRange?: string, range?: Range) => {
        if (!selectedRange) {
            return EMPTY_PLACEHOLDER
        }
        const selectedPredefinedRange = predefinedRanges.find(
            (range) => range.value === selectedRange,
        )
        if (!selectedPredefinedRange) {
            if (!range) {
                return EMPTY_PLACEHOLDER
            } else {
                return `${moment(range.startDate).format(
                    dateDisplayFormat || DATE_FORMAT,
                )} - ${moment(range.endDate).format(dateDisplayFormat || DATE_FORMAT)}`
            }
        } else {
            if (defaultLabel && selectedPredefinedRange.value === defaultRange) {
                return defaultLabel
            }
            return selectedPredefinedRange.label
        }
    }

    const updateRanges = (start: Date | undefined, end: Date | undefined) => {
        setDateRange({
            startDate: start,
            endDate: end,
        })
        setLocalDateRange({
            startDate: start,
            endDate: end,
        })
    }

    useEffect(() => {
        const start = predefinedDateRange?.startDate
        const end = predefinedDateRange?.endDate
        if (start && end) {
            setLocalDateRange(predefinedDateRange)
            const timePeriodDays = moment(end).diff(moment(start), 'days', true)
            const endsToday = moment(end).toDate().toDateString() === new Date().toDateString()

            if (Math.abs(timePeriodDays - 30) <= 1 && endsToday) {
                setButtonLabel(getButtonLabel(PredefinedRange.THIRTY_DAYS, predefinedDateRange))
                setPredefinedRange(PredefinedRange.THIRTY_DAYS)
            } else if (Math.abs(timePeriodDays - 90) <= 1 && endsToday) {
                setButtonLabel(getButtonLabel(PredefinedRange.NINETY_DAYS, predefinedDateRange))
                setPredefinedRange(PredefinedRange.NINETY_DAYS)
            } else if (Math.abs(timePeriodDays - 365) <= 7 && endsToday) {
                setButtonLabel(getButtonLabel(PredefinedRange.TWELVE_MONTHS, predefinedDateRange))
                setPredefinedRange(PredefinedRange.TWELVE_MONTHS)
            } else {
                setButtonLabel(getButtonLabel(PredefinedRange.CUSTOM, predefinedDateRange))
                setPredefinedRange(PredefinedRange.CUSTOM)
            }
        } else {
            setButtonLabel(getButtonLabel(defaultRange))
        }
        setTimeout(() => {
            setInitLoad(false)
        }, 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultRange, predefinedDateRange])

    useUpdateEffect(() => {
        if (predefinedRange && !initLoad) {
            if (predefinedRange !== PredefinedRange.CUSTOM) {
                let startDate: Date
                switch (predefinedRange) {
                    case PredefinedRange.THIRTY_DAYS:
                        startDate = today.clone().subtract(30, 'days').toDate()
                        break
                    case PredefinedRange.NINETY_DAYS:
                        startDate = today.clone().subtract(90, 'days').toDate()
                        break
                    case PredefinedRange.TWELVE_MONTHS:
                        startDate = today.clone().subtract(12, 'months').toDate()
                        break
                    default:
                        startDate = today.toDate()
                }
                updateRanges(startDate, today.toDate())
            }
        }
    }, [predefinedRange])

    return (
        <DropdownNew
            leftIcon={<EventOutlinedIcon sx={{ fill: palette.TextTertiary }} />}
            buttonLabel={buttonLabel || ''}
            buttonSize={'sm'}
            buttonVariant={'secondary'}
            buttonStyle={'outline'}
            closeOnClick={false}
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                ...sx,
            }}
            paperSx={{
                '.MuiList-root': {
                    pt: 0,
                    pb: 0,
                },
            }}
            labelSx={{
                color: `TextPrimary`,
                ...labelSx,
            }}
        >
            <Box sx={{ padding: '16px' }}>
                <ButtonGroupNew
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                        mb: 3,
                    }}
                    items={predefinedRanges}
                    onChange={(predefinedRange) => {
                        setPredefinedRange(predefinedRange)
                        setButtonLabel(getButtonLabel(predefinedRange))
                    }}
                    value={predefinedRange || ''}
                />
                <DateRangePicker
                    onChange={(range) => {
                        setPredefinedRange(PredefinedRange.CUSTOM)
                        setButtonLabel(getButtonLabel(PredefinedRange.CUSTOM, range))
                        updateRanges(range.startDate, range.endDate)
                    }}
                    predefinedRange={{
                        startDate: localDateRange.startDate!,
                        endDate: localDateRange.endDate!,
                    }}
                />
            </Box>
        </DropdownNew>
    )
}
export default DateRangeFilterNew
